import { React, useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Stack,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import { EventNote, InfoOutlined, Add } from "@mui/icons-material";

import NotaCard from "../../components/NotaCard";
import ModalNota from "../../components/ModalNota";
import GenericPieChart from "../../components/chart/GenericPieChart";
import ModalPatients from "../../components/patients/ModalPatients";
import { opcoesListaPacientes } from "../../utils/labels";
import api from "../../services/api";
import dayjs from "dayjs";
import { getItemDecrypt } from "../../utils/cryptoData";

const styles = {
  agendaIconRed: {
    backgroundColor: "#D92424",
    borderRadius: 12,
    "&.MuiButtonBaseRoot:hover": { bgcolor: "red" },
  },
  agendaIconBlue: {
    backgroundColor: "#3E94C2",
    borderRadius: 12,
    "&.MuiButtonBaseRoot:hover": { bgcolor: "red" },
  },
  title: {
    fontWeight: "700",
    fontSize: "18px",
    lineHeight: "25px",
  },
  subtitle: {
    fontWeight: "600",
    fontSize: "15px",
    lineHeight: "22px",
    textTransform: "none",
    paddingLeft: "0px",
    paddingRight: "2px",
    marginRight: "20px",
  },
  divider: {
    height: 2,
    width: "75px",
    marginTop: 1,
    borderRadius: 2,
  },
  boxInfo: { border: 1, padding: 2, borderRadius: 1 },
  boxInfoAzul: { border: 1, padding: 2, borderRadius: 1, color: "#3E94C2" },
};

const notaAssunstos = [
  {
    assunto: "Consulta",
    id: 1,
  },
  {
    assunto: "Medicamento",
    id: 2,
  },
  {
    assunto: "Medicação e Consulta",
    id: 3,
  },
  {
    assunto: "Outros",
    id: 4,
  },
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function AcessarListagem({ color = "#3E94C2", handleOnClick }) {
  return (
    <Stack
      direction="row"
      alignItems={"center"}
      spacing={1}
      sx={{ fontSize: 12, "&:hover": { cursor: "pointer" } }}
      onClick={handleOnClick}
    >
      <Box>Acessar listagem</Box>
      <InfoOutlined sx={{ color: color }} fontSize="small" />
    </Stack>
  );
}

const CardHomeSmall = ({ color, title, handleOnClick, numberInfo }) => {
  return (
    <Box sx={styles.boxInfo} color={color} width={"100%"} height="152.83px">
      <Box style={styles.title}>{title}</Box>
      <Divider color={color} sx={styles.divider} />
      <AcessarListagem color={color} handleOnClick={handleOnClick} />
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Box fontWeight={"700"} fontSize={{ xs: "32px", md: "46px" }}>
          {numberInfo}
        </Box>
        <IconButton
          style={{
            backgroundColor: color,
            borderRadius: 8,
            "&.MuiButtonBaseRoot:hover": { bgcolor: color },
            width: { xs: "32px", md: "40px" },
          }}
        >
          <EventNote sx={{ color: "white" }} />
        </IconButton>
      </Stack>
    </Box>
  );
};

const HomeIn = () => {
  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);
  const [openPacientesAtraso, setOpenPacientesAtraso] = useState(false);
  const [openConsultasDia, setOpenConsultasDia] = useState(false);
  const [openPacientesTratamento, setOpenPacientesTratamento] = useState(false);
  const [emTratamento, setEmTratamento] = useState(0);
  const [emAtraso, setEmAtrasado] = useState(0);
  const [consultaDoDia, setConsultaDodia] = useState([]);
  const [busca, setBusca] = useState([]);
  const [notas, setNotas] = useState([]);
  const [totalNotasNaoArquivadas, setTotalNotasNaoArquivadas] = useState(0);
  const [totalNotasArquivadas, setTotalNotasArquivadas] = useState(0);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleOpenPacientesAtraso = () => setOpenPacientesAtraso(true);
  const handleClosePacientesAtraso = () => setOpenPacientesAtraso(false);
  const handleOpenConsultasDia = () => setOpenConsultasDia(true);
  const handleCloseConsultasDia = () => setOpenConsultasDia(false);
  const handleOpenPacientesTratamento = () => setOpenPacientesTratamento(true);
  const handleClosePacientesTratamento = () =>
    setOpenPacientesTratamento(false);
  const [loading, setLoading] = useState(false);
  const [pacientesEmAtraso, setPacientesEmAtraso] = useState([]);
  const [pacientesEmTratamento, setPacientesEmTratamento] = useState([]);
  const [pacientesConsultasDoDia, setPacientesConsultasDoDia] = useState([]);

  const vinculoAtivo = getItemDecrypt("vinculo_ativo");

  const [pieChartWidthSmall, setPieChartWidthSmall] = useState(false);

  async function getPacientesEmAtraso() {
    try {
      const { data } = await api.post("/pacientes/", {
        lateOnly: true,
        id_estabelecimento_saude: vinculoAtivo.estabelecimento.id,
      });
      setPacientesEmAtraso(data);
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  async function getPacientesEmTratamento() {
    try {
      const { data } = await api.post("/pacientes/", {
        inTreatment: true,
        id_estabelecimento_saude: vinculoAtivo.estabelecimento.id,
      });
      setPacientesEmTratamento(data);
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  async function getPacientesConsultasDoDia() {
    try {
      const { data } = await api.post("/pacientes/", {
        appointmentForToday: true,
        id_vinculo_ps: vinculoAtivo.id,
      });
      setPacientesConsultasDoDia(data);
    } catch (error) {
      console.error("Ocorreu um erro:", error.message);
    }
  }

  async function handleNotas(params) {
    try {
      const response = await api.post("getNota", { co_id: vinculoAtivo.id });
      setNotas(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  async function handleDados(params) {
    try {
      const response = await api.post("/dashHome", {
        id_vinculo: vinculoAtivo.id,
      });
      if (response.data) {
        setEmTratamento(response.data.emTratamento);
        setEmAtrasado(response.data.emAtraso);
        setBusca(response.data.busca);
        setConsultaDodia(response.data.result);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleTipoListagem = () => {
    if (openPacientesAtraso) {
      return opcoesListaPacientes.EM_ATRASO;
    } else if (openPacientesTratamento) {
      return opcoesListaPacientes.EM_TRATAMENTO;
    } else if (openConsultasDia) {
      return opcoesListaPacientes.CONSULTAS_DO_DIA;
    } else {
      return null;
    }
  };

  // fazendo otimizacoes
  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        handleNotas(),
        handleDados(),
        getPacientesEmAtraso(),
        getPacientesEmTratamento(),
        getPacientesConsultasDoDia(),
      ]);
    } catch (error) {
      console.error("An error occurred during data fetching:", error.message);
    } finally {
      setLoading(false);
    }
  };

  //localhost:3334/dashHome
  useEffect(() => {
    // setLoading(true);
    // handleNotas();
    // handleDados();
    // setLoading(false);
    // getPacientesEmAtraso();
    // getPacientesEmTratamento();
    // getPacientesConsultasDoDia();
    fetchData();
  }, []);

  const handleResize = () => {
    // if (window.innerWidth < 600) {
    //   console.log("< 600");
    //   setPieChartWidthSmall((oldvalue) => true);
    // } else if (window.innerWidth >= 600) {
    //   console.log(">= 600");
    //   setPieChartWidthSmall((oldvalue) => false);
    // }
  };

  useEffect(() => {
    // Adiciona um ouvinte de evento para atualizar o tamanho da tela quando a janela é redimensionada
    window.addEventListener("resize", handleResize);

    // Remove o ouvinte de evento ao desmontar o componente
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    handleTotalNotas();
  }, [notas]);

  const handleTotalNotas = () => {
    if (notas) {
      const notasNaoArquivadas = notas.filter(
        (nota) => nota.nu_status === "0"
      ).length;
      const notasArquivadas = notas.filter(
        (nota) => nota.nu_status === "1"
      ).length;

      setTotalNotasNaoArquivadas(notasNaoArquivadas);
      setTotalNotasArquivadas(notasArquivadas);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    !loading && (
      <>
        <Stack
          direction={"column"}
          spacing={2}
          maxWidth="1250px"
          width="100%"
          boxSizing={"border-box"}
        >
          <Box>
            <Stack
              display="flex"
              gap={"24px"}
              boxSizing={"border-box"}
              flexDirection={"row"}
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              width="100%"
            >
              <Box
                width={"100%"}
                maxWidth={{ xs: "100%", sm: "200px", md: "264px" }}
              >
                <Stack
                  direction={{ xs: "column", sm: "column" }}
                  width={{ xs: "100%", sm: "200px", md: "264px" }}
                  gap={"16px"}
                >
                  <CardHomeSmall
                    title="Pacientes em tratamento"
                    handleOnClick={handleOpenPacientesTratamento}
                    color="#3E94C2"
                    numberInfo={emTratamento}
                  />
                  <CardHomeSmall
                    title="Pacientes em atraso"
                    handleOnClick={handleOpenPacientesAtraso}
                    color="#D92424"
                    numberInfo={emAtraso}
                  />
                </Stack>
              </Box>
              <Box
                sx={styles.boxInfoAzul}
                maxWidth={{
                  xs: "100%",
                  sm: "calc(100% - 225px)",
                  md: "calc(100% - 288px)",
                  lg: "400px",
                }}
                width={"100%"}
                height="321px"
              >
                <Box style={styles.title}>Busca ativa do paciente</Box>
                <Box display={"flex"} justifyContent={"center"}>
                  <GenericPieChart
                    PieChartWidth={360}
                    data={busca}
                    COLORS={["#3E94C2", "#D92424"]}
                  />
                </Box>
              </Box>
              <Box
                sx={styles.boxInfoAzul}
                maxWidth={{ xs: "100%", lg: "538px" }}
                width={"100%"}
              >
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Box style={styles.title}>Consultas para o dia</Box>
                  <AcessarListagem handleOnClick={handleOpenConsultasDia} />
                </Stack>
                <Box display={"flex"} justifyContent={"center"}>
                  <GenericPieChart
                    PieChartWidth={360}
                    data={consultaDoDia}
                    COLORS={["#3E94C2", "#FCCB30", "#9D9D9D", "#FF0000"]}
                  />
                </Box>
              </Box>
            </Stack>
          </Box>
          <Box sx={styles.boxInfoAzul} style={{ width: "100%" }}>
            <Stack direction={"column"}>
              <Box>
                <Stack
                  direction={"row"}
                  spacing={4}
                  alignItems={"center"}
                  sx={{ marginBottom: 2 }}
                >
                  <Box style={styles.title} sx={{ color: "#1E729F" }}>
                    Bloco de notas (
                    {totalNotasArquivadas + totalNotasNaoArquivadas})
                  </Box>
                  <Button
                    variant="contained"
                    endIcon={<Add />}
                    style={{ textTransform: "none", bgcolor: "#1E729F" }}
                    onClick={handleOpen}
                  >
                    Adicionar nota
                  </Button>
                </Stack>
              </Box>
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab
                      label={`Notas atuais (${totalNotasNaoArquivadas})`}
                      {...a11yProps(0)}
                      style={styles.subtitle}
                    />
                    <Tab
                      label={`Notas arquivadas (${totalNotasArquivadas})`}
                      {...a11yProps(1)}
                      style={styles.subtitle}
                    />
                  </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                  <Stack
                    display="grid"
                    gap={"16px 40px"}
                    gridTemplateColumns={{
                      xs: "repeat(1, 1fr)",
                      md: "repeat(2, 1fr)",
                    }}
                  >
                    {Array.isArray(notas) &&
                      notas
                        .filter((nota) => nota.nu_status === "0")
                        .map((nota) => {
                          const assunto = notaAssunstos.map((notaAssunto) => {
                            if (
                              notaAssunto.id ===
                              parseInt(nota.co_categoria_assunto)
                            ) {
                              return notaAssunto.assunto;
                            }
                          });

                          return (
                            <NotaCard
                              notas={notas}
                              setNotas={setNotas}
                              co_id={nota.co_id}
                              key={nota.id_autor_vinculo}
                              nu_status={nota.nu_status}
                              title={assunto}
                              data={dayjs(nota.dt_criado_em)
                                .format("DD/MM/YYYY")
                                .toString()}
                              description={nota.no_descricao}
                            />
                          );
                        })}
                  </Stack>
                </TabPanel>
                <TabPanel value={value} index={1}>
                  <Stack
                    display="grid"
                    gap={"16px 40px"}
                    gridTemplateColumns={{
                      xs: "repeat(1, 1fr)",
                      md: "repeat(2, 1fr)",
                    }}
                  >
                    {Array.isArray(notas) &&
                      notas
                        .filter((nota) => nota.nu_status === "1")
                        .map((nota) => {
                          const assunto = notaAssunstos.map((notaAssunto) => {
                            if (
                              notaAssunto.id ===
                              parseInt(nota.co_categoria_assunto)
                            ) {
                              return notaAssunto.assunto;
                            }
                          });

                          return (
                            <NotaCard
                              notas={notas}
                              setNotas={setNotas}
                              co_id={nota.co_id}
                              key={nota.id_autor_vinculo}
                              nu_status={nota.nu_status}
                              title={assunto}
                              data={dayjs(nota.dt_criado_em)
                                .format("DD/MM/YYYY")
                                .toString()}
                              description={nota.no_descricao}
                            />
                          );
                        })}
                  </Stack>
                </TabPanel>
              </Box>
            </Stack>
          </Box>
          <ModalNota
            openModal={open}
            setOpenModal={handleClose}
            notas={notas}
            setNotas={setNotas}
          ></ModalNota>
        </Stack>
        <ModalPatients
          openModal={openPacientesAtraso}
          setOpenModal={handleClosePacientesAtraso}
          descricao={"Pacientes em atraso"}
          dados={pacientesEmAtraso}
          tipoListagem={handleTipoListagem()}
        />
        <ModalPatients
          openModal={openPacientesTratamento}
          setOpenModal={handleClosePacientesTratamento}
          descricao={"Pacientes em tratamento"}
          dados={pacientesEmTratamento}
          tipoListagem={handleTipoListagem()}
        />
        <ModalPatients
          openModal={openConsultasDia}
          setOpenModal={handleCloseConsultasDia}
          descricao={"Consultas do dia"}
          dados={pacientesConsultasDoDia}
          tipoListagem={handleTipoListagem()}
        />
      </>
    )
  );
};

export default HomeIn;
